import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static targets = ["timelineDotTrack", "timelineProgress", "update"];
    static values = {
        targetId: String,
        markSeenUrl: String,
    };

    declare readonly timelineDotTrackTarget: HTMLElement;
    declare readonly hasTimelineDotTrackTarget: boolean;
    declare readonly timelineProgressTarget: HTMLElement;
    declare readonly hasTimelineProgressTarget: boolean;
    declare readonly updateTargets: HTMLElement[];
    declare readonly targetIdValue: string;
    declare readonly hasTargetIdValue: boolean;
    declare readonly markSeenUrlValue: string;
    declare readonly hasMarkSeenUrlValue: boolean;

    private observer?: IntersectionObserver;
    private timelineResizeObserver?: ResizeObserver;
    private animationFrame?: number;
    private timelineAnimationFrame?: number;
    private readonly markedUpdateIds = new Set<string>();

    connect(): void {
        for (const update of this.updateTargets) {
            const updateId = update.dataset.changelogUpdateId;
            if (updateId && update.dataset.changelogUpdateSeen === "true") {
                this.markedUpdateIds.add(updateId);
            }
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }

                    const update = entry.target as HTMLElement;
                    const updateId = update.dataset.changelogUpdateId;
                    if (updateId) {
                        this.markSeen(updateId, update);
                    }
                }
            },
            { threshold: 0.1 }
        );

        for (const update of this.updateTargets) {
            this.observer.observe(update);
        }

        if (this.hasTimelineDotTrackTarget) {
            this.updateTimelineDotTrack();
            this.updateTimelineProgress();
            this.timelineResizeObserver = new ResizeObserver(() => {
                this.updateTimelineDotTrack();
                this.updateTimelineProgress();
            });
            const timeline = this.timelineDotTrackTarget.offsetParent;
            if (timeline instanceof HTMLElement) {
                this.timelineResizeObserver.observe(timeline);
            }
        }

        this.animationFrame = window.requestAnimationFrame(() => {
            if (!this.hasTargetIdValue || !this.targetIdValue) {
                return;
            }

            document.getElementById(this.targetIdValue)?.scrollIntoView({
                behavior: "auto",
                block: "start",
            });
        });
    }

    disconnect(): void {
        this.observer?.disconnect();
        this.timelineResizeObserver?.disconnect();
        if (this.animationFrame !== undefined) {
            window.cancelAnimationFrame(this.animationFrame);
        }
        if (this.timelineAnimationFrame !== undefined) {
            window.cancelAnimationFrame(this.timelineAnimationFrame);
        }
    }

    updateTimelineProgress(): void {
        if (this.timelineAnimationFrame !== undefined) {
            return;
        }

        this.timelineAnimationFrame = window.requestAnimationFrame(() => {
            this.timelineAnimationFrame = undefined;
            this.renderTimelineProgress();
        });
    }

    private updateTimelineDotTrack(): void {
        const oldestUpdate = this.updateTargets[this.updateTargets.length - 1];
        const timeline = this.timelineDotTrackTarget.offsetParent;
        const dot = this.timelineDotTrackTarget.firstElementChild;
        if (!oldestUpdate || !(timeline instanceof HTMLElement) || !(dot instanceof HTMLElement)) {
            return;
        }

        const oldestDateDotTop = oldestUpdate.offsetTop + 6;
        this.timelineDotTrackTarget.style.bottom = `${timeline.clientHeight - oldestDateDotTop - dot.offsetHeight}px`;
    }

    private renderTimelineProgress(): void {
        const dot = this.timelineDotTrackTarget.firstElementChild;
        const timeline = this.timelineDotTrackTarget.offsetParent;
        if (!this.hasTimelineProgressTarget || !(dot instanceof HTMLElement) || !(timeline instanceof HTMLElement)) {
            return;
        }

        const dotBounds = dot.getBoundingClientRect();
        const dotCenter = dotBounds.top + dotBounds.height / 2 - timeline.getBoundingClientRect().top;
        this.timelineProgressTarget.style.height = `${Math.max(0, dotCenter - this.timelineDotTrackTarget.offsetTop)}px`;
    }

    private markSeen(updateId: string, update: HTMLElement): void {
        if (this.markedUpdateIds.has(updateId) || !this.hasMarkSeenUrlValue || !this.markSeenUrlValue) {
            return;
        }

        this.markedUpdateIds.add(updateId);

        void fetch(this.markSeenUrlValue, {
            method: "POST",
            credentials: "same-origin",
            keepalive: true,
            body: new URLSearchParams({ updateId }),
        })
            .then((response) => {
                if (!response.ok) {
                    this.markedUpdateIds.delete(updateId);
                    return;
                }

                update.dataset.changelogUpdateSeen = "true";
            })
            .catch(() => {
                this.markedUpdateIds.delete(updateId);
            });
    }
}
